import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  Settings, 
  Save,
  Loader2,
  Layers,
  CreditCard,
  ExternalLink,
  Calendar
} from 'lucide-react';

const SchoolSettingsPage = () => {
  const { user } = useAuth();
  const [school, setSchool] = useState(null);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [initiatingPayment, setInitiatingPayment] = useState(false);

  const isSchoolAdmin = user?.role === 'school_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schoolRes, levelsRes, subRes] = await Promise.all([
        api.get(`/schools/${user.school_id}`),
        api.get(`/schools/${user.school_id}/levels`),
        api.get('/auth/subscription-info')
      ]);
      setSchool(schoolRes.data);
      setLevels(levelsRes.data);
      setSubscriptionInfo(subRes.data);
    } catch (error) {
      toast.error('Failed to fetch school data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLevels = async () => {
    setSaving(true);
    try {
      await api.put(`/schools/${user.school_id}/levels`, levels);
      toast.success('School levels updated');
    } catch (error) {
      toast.error('Failed to update school levels');
    } finally {
      setSaving(false);
    }
  };

  const handleLevelChange = (index, field, value) => {
    const newLevels = [...levels];
    newLevels[index] = { ...newLevels[index], [field]: field === 'level_id' ? parseInt(value) : value };
    setLevels(newLevels);
  };

  const addLevel = () => {
    const maxId = Math.max(...levels.map(l => l.level_id), -1);
    setLevels([...levels, { level_id: maxId + 1, description: '' }]);
  };

  const removeLevel = (index) => {
    setLevels(levels.filter((_, i) => i !== index));
  };

  const handleMakePayment = async () => {
    setInitiatingPayment(true);
    try {
      // Send notification email first
      await api.post('/auth/payment-initiated');
      toast.success('Payment notification sent. Redirecting to payment page...');
      // Open payment link in new tab
      window.open('https://wise.com/pay/r/frHEQtOCSPOj5RA', '_blank');
    } catch (error) {
      // Still open payment link even if notification fails
      window.open('https://wise.com/pay/r/frHEQtOCSPOj5RA', '_blank');
    } finally {
      setInitiatingPayment(false);
    }
  };

  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getDaysRemaining = () => {
    if (!subscriptionInfo?.license_expiry_date) return null;
    const expiry = new Date(subscriptionInfo.license_expiry_date);
    const now = new Date();
    return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  };

  const daysRemaining = getDaysRemaining();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="school-settings-page">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-slate-900">School Settings</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Manage your school configuration</p>
        </div>

        {/* Subscription Info - Only for School Admins */}
        {isSchoolAdmin && subscriptionInfo && (
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit flex items-center gap-2 text-lg sm:text-xl">
                <CreditCard className="h-5 w-5" />
                Subscription
              </CardTitle>
              <CardDescription>Your school's subscription status and expiry</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="space-y-1">
                  <Label className="text-slate-500 text-sm">Status</Label>
                  <p className={`text-base sm:text-lg font-semibold capitalize ${
                    subscriptionInfo.subscription_status === 'active' ? 'text-emerald-600' :
                    subscriptionInfo.subscription_status === 'trial' ? 'text-amber-600' :
                    'text-rose-600'
                  }`}>
                    {subscriptionInfo.subscription_status || 'Trial'}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-500 text-sm">Expiry Date</Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <p className={`text-base sm:text-lg font-semibold ${
                      daysRemaining && daysRemaining <= 14 ? 'text-amber-600' :
                      daysRemaining && daysRemaining <= 0 ? 'text-rose-600' :
                      'text-slate-900'
                    }`}>
                      {formatExpiryDate(subscriptionInfo.license_expiry_date)}
                    </p>
                  </div>
                  {daysRemaining !== null && (
                    <p className={`text-xs sm:text-sm ${
                      daysRemaining <= 0 ? 'text-rose-500' :
                      daysRemaining <= 14 ? 'text-amber-500' :
                      'text-slate-500'
                    }`}>
                      {daysRemaining <= 0 
                        ? 'Subscription expired' 
                        : `${daysRemaining} days remaining`}
                    </p>
                  )}
                </div>
                <div className="flex items-center sm:col-span-2 lg:col-span-1">
                  <Button
                    onClick={handleMakePayment}
                    disabled={initiatingPayment}
                    className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto text-sm"
                    data-testid="make-payment-btn"
                  >
                    {initiatingPayment ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="h-4 w-4 mr-2" />
                    )}
                    <span className="truncate">Extend Subscription (1 Year)</span>
                  </Button>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-slate-500 mt-4">
                Click the button above to make a payment and extend your subscription by 1 year. Your subscription will be updated after payment confirmation.
              </p>
            </CardContent>
          </Card>
        )}

        {/* School Info */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2 text-lg sm:text-xl">
              <Settings className="h-5 w-5" />
              School Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">School Name</Label>
              <Input value={school?.name || ''} disabled className="text-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Address</Label>
              <Input value={school?.address || ''} disabled className="text-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Contact Person</Label>
              <Input value={school?.contact_person || ''} disabled className="text-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Contact Email</Label>
              <Input value={school?.contact_email || ''} disabled className="text-sm" />
            </div>
          </CardContent>
        </Card>

        {/* School Levels */}
        <Card>
          <CardHeader>
            <div className="space-y-3">
              <div>
                <CardTitle className="font-outfit flex items-center gap-2 text-lg sm:text-xl">
                  <Layers className="h-5 w-5" />
                  School Levels
                </CardTitle>
                <CardDescription className="text-sm">Configure school level IDs and descriptions</CardDescription>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs sm:text-sm text-blue-800">
                  To view the school levels configured in your Veracross database, visit:{' '}
                  <a 
                    href="https://axiom.veracross.com/#/results/4609" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 font-medium underline break-all"
                    data-testid="veracross-levels-link"
                  >
                    https://axiom.veracross.com/#/results/4609
                  </a>
                  {' '}(opens in a new tab) and make sure that all school levels from your Veracross database appear below.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" onClick={addLevel} data-testid="add-level-btn" size="sm" className="text-sm">
                  Add Level
                </Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-sm"
                  onClick={handleSaveLevels}
                  disabled={saving}
                  data-testid="save-levels-btn"
                  size="sm"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Levels
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24 sm:w-32 text-xs sm:text-sm">Level ID</TableHead>
                  <TableHead className="text-xs sm:text-sm">Description</TableHead>
                  <TableHead className="w-20 sm:w-24 text-xs sm:text-sm">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {levels.map((level, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Input
                        type="number"
                        value={level.level_id}
                        onChange={(e) => handleLevelChange(index, 'level_id', e.target.value)}
                        className="w-16 sm:w-20 text-sm"
                        data-testid={`level-id-${index}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={level.description}
                        onChange={(e) => handleLevelChange(index, 'description', e.target.value)}
                        placeholder="e.g., Lower School"
                        data-testid={`level-desc-${index}`}
                        className="text-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-rose-600 text-xs sm:text-sm px-2"
                        onClick={() => removeLevel(index)}
                        data-testid={`remove-level-${index}`}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SchoolSettingsPage;
